/**
 * Utility functions for storing and managing AI chat messages
 * Uses localStorage to persist conversations per seance
 */

const STORAGE_PREFIX = 'ai_chat_';
const MAX_STORED_SEANCES = 10; // Maximum number of seance conversations to keep
const MAX_AGE_DAYS = 30; // Maximum age in days for stored conversations

/**
 * Get storage key for a seance
 */
function getStorageKey(seanceId) {
  return `${STORAGE_PREFIX}${seanceId}`;
}

/**
 * Get all stored chat keys
 */
function getAllChatKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Clean up old conversations
 * Keeps only the most recent MAX_STORED_SEANCES conversations
 */
export function cleanupOldConversations() {
  try {
    const allKeys = getAllChatKeys();
    const conversations = [];

    // Get all conversations with their timestamps
    for (const key of allKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && data.timestamp) {
          conversations.push({
            key,
            timestamp: data.timestamp,
            seanceId: key.replace(STORAGE_PREFIX, '')
          });
        }
      } catch (e) {
        // Invalid data, remove it
        localStorage.removeItem(key);
      }
    }

    // Sort by timestamp (newest first)
    conversations.sort((a, b) => b.timestamp - a.timestamp);

    // Remove conversations older than MAX_AGE_DAYS
    const maxAge = Date.now() - (MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
    conversations.forEach(conv => {
      if (conv.timestamp < maxAge) {
        localStorage.removeItem(conv.key);
      }
    });

    // Keep only the most recent MAX_STORED_SEANCES
    const remainingKeys = getAllChatKeys();
    if (remainingKeys.length > MAX_STORED_SEANCES) {
      const remainingConversations = remainingKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return {
            key,
            timestamp: data?.timestamp || 0
          };
        } catch {
          return { key, timestamp: 0 };
        }
      }).sort((a, b) => b.timestamp - a.timestamp);

      // Remove oldest conversations beyond the limit
      remainingConversations.slice(MAX_STORED_SEANCES).forEach(conv => {
        localStorage.removeItem(conv.key);
      });
    }
  } catch (error) {
    console.error('Error cleaning up old conversations:', error);
  }
}

/**
 * Save messages for a seance
 */
export function saveMessages(seanceId, messages) {
  try {
    if (!seanceId) return;
    
    const key = getStorageKey(seanceId);
    const data = {
      messages,
      timestamp: Date.now(),
      seanceId
    };
    
    localStorage.setItem(key, JSON.stringify(data));
    
    // Clean up old conversations periodically
    // Only do this occasionally to avoid performance issues
    if (Math.random() < 0.1) { // 10% chance
      cleanupOldConversations();
    }
  } catch (error) {
    console.error('Error saving messages:', error);
  }
}

/**
 * Load messages for a seance
 */
export function loadMessages(seanceId) {
  try {
    if (!seanceId) return [];
    
    const key = getStorageKey(seanceId);
    const data = localStorage.getItem(key);
    
    if (!data) return [];
    
    const parsed = JSON.parse(data);
    
    // Check if data is too old
    const maxAge = Date.now() - (MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
    if (parsed.timestamp && parsed.timestamp < maxAge) {
      localStorage.removeItem(key);
      return [];
    }
    
    return parsed.messages || [];
  } catch (error) {
    console.error('Error loading messages:', error);
    return [];
  }
}

/**
 * Clear messages for a seance
 */
export function clearMessages(seanceId) {
  try {
    if (!seanceId) return;
    
    const key = getStorageKey(seanceId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing messages:', error);
  }
}

